import React, { useEffect, useRef, useState } from 'react'

import isLoudVolume, { isLoudVolumeButAcceptable } from '../functions/isLoudVolume'
import isSoftVolume from '../functions/isSoftVolume'
import useStudentPreChecks from '../../useStudentPreChecks'
import { PRE_CHECK_MACHINE_STATES } from '../../constants'
import { MAX_COUNT_TO_EVALUATE_BEFORE_CALLING_TEACHER, MS_TO_WAIT_BETWEEN_EVALUATE_FOR_VOLUME_PROBLEM, VOLUME_CHECK_EVENTS, SPEECH_VOLUME_CHECK_STATES } from '../StudentVolumeChecksConstants'

import { COPY_STRINGS } from '../../../../constants/translatedCopy'
import isAcceptableVolume from '../functions/isAcceptableVolume'
import { getCopyByGradeband } from '../../../../services/ThemingService'
import didSaySomething from '../functions/didSaySomething'
import { updateCheckData } from '../../studentPreChecksSlice'
import { useDispatch } from 'react-redux'

export default function VolumeEvaluationTimer(props) {

  const { speechProps, volumeStats, studentSpeechVolumeEvaluationCount, isTeacherMode, setShowTeacherTroubleshootScreen, onAcceptableVolumeCheckPassed, setCurrentSpeechVolumeCheckState, resetDidSaySomething, logVolumeCheckFailure } = props

    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks });
    const [volumeMeasurementDone, setVolumeMeasurementDone] = useState(false)

    const waitToEvaluateVolumeProblemsTimer = useRef()

    const dispatch = useDispatch();

    useEffect(() => {
        startWaitToEvaluateVolumeProblems()

        return () => {
            cancelWaitToEvaluateVolumeProblems()
        }
    }, [])

    useEffect(() => {
        if(volumeMeasurementDone){
            evaluateVolume()
        }
    }, [volumeMeasurementDone])

    const startWaitToEvaluateVolumeProblems = () => {
        setVolumeMeasurementDone(false)
        cancelWaitToEvaluateVolumeProblems()
        
        // Check if we've already reached the max count to prevent race conditions
        if(studentSpeechVolumeEvaluationCount.current < MAX_COUNT_TO_EVALUATE_BEFORE_CALLING_TEACHER){
            waitToEvaluateVolumeProblemsTimer.current = setTimeout(()=>{
                setVolumeMeasurementDone(true)
            }, MS_TO_WAIT_BETWEEN_EVALUATE_FOR_VOLUME_PROBLEM)
        }
    }
    const cancelWaitToEvaluateVolumeProblems = () => {
        if (waitToEvaluateVolumeProblemsTimer.current) {
            clearTimeout(waitToEvaluateVolumeProblemsTimer.current)
            waitToEvaluateVolumeProblemsTimer.current = null
        }
    }
    const evaluateVolume = async () => {
        // Increment the count before evaluating to prevent race conditions
        const currentCount = studentSpeechVolumeEvaluationCount.current
        studentSpeechVolumeEvaluationCount.current = currentCount + 1
        
        if(isAcceptableVolume(volumeStats)){
            await handleAcceptableVolume(volumeStats)
        }else if(isSoftVolume(volumeStats)) {
            await handleTooSoft(currentCount)
        }else if(isLoudVolume(volumeStats)) {
            await handleTooLoud(currentCount)
        }else{
            await handleOtherVolumeConditions(currentCount)
        }
    }

    const handleOtherVolumeConditions = async (currentCount) => {
        studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_NEITHER_SOFT_NOR_LOUD : VOLUME_CHECK_EVENTS.NEITHER_SOFT_NOR_LOUD)
        // force pass as fallback because it's not too loud NOR too soft, it must be in between.
        // we will use the logged volume stats to determine how it ended up here.
        onAcceptableVolumeCheckPassed()
        studentPreCheck.addTroubleshootData({
            ready_unhandledVolume: {
                volumeSnapshot: volumeStats,
                trialCount: currentCount
            }
        })
    }

    const handleAcceptableVolume = async () => {
        // if the max / min volume delta is too small, log the event
        if (!didSaySomething(volumeStats)){
            studentPreCheck.logEvent(
                isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_VOLUME_CHECK_LOW_DELTA_BYPASSED : VOLUME_CHECK_EVENTS.VOLUME_CHECK_LOW_DELTA_BYPASSED,
                undefined,
                {
                    maxVolume: volumeStats?.maxVolume,
                    minVolume: volumeStats?.minVolume,
                }
            );
            dispatch(updateCheckData({
                type: PRE_CHECK_MACHINE_STATES.studentASRChecks,
                data: {
                    volumeStats: {
                        maxVolume: volumeStats?.maxVolume,
                        minVolume: volumeStats?.minVolume,
                    },
                    eventTimeStamp: Date.now(),
                    event: isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_VOLUME_CHECK_LOW_DELTA_BYPASSED : VOLUME_CHECK_EVENTS.VOLUME_CHECK_LOW_DELTA_BYPASSED
                }
            }));
        }
        // still falls within the acceptable volume, but is it on the high side
        if(isLoudVolumeButAcceptable(volumeStats)){
            studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_TEST_ALMOST_TOO_LOUD : VOLUME_CHECK_EVENTS.ALMOST_TOO_LOUD)
            onAcceptableVolumeCheckPassed({loudButAcceptable: true})
        }else{
            onAcceptableVolumeCheckPassed()
        }
    }

    const handleTooSoft = async (currentCount) => {
        studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_TEST_TOO_SOFT : VOLUME_CHECK_EVENTS.TOO_SOFT)

        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
        if(isTeacherMode){
            await speechProps.speakWithPromise({ textToSpeak: studentChecks.teacher_mode_volume_failure_one })
            setShowTeacherTroubleshootScreen(true)
        }else{
            studentPreCheck.addTroubleshootData({
                ready_handle_too_soft: {
                    volumeSnapshot: volumeStats,
                    trialCount: currentCount
                }
            })
            if(currentCount === 0){
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_soft_one })
                resetDidSaySomething()
                setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH)
            }else if(currentCount === 1){
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_soft_second_round })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_soft_second_round_two })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_soft_second_round_three })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_soft_second_round_four })
                resetDidSaySomething()
                setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH)
            } else if(currentCount === 2){
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_get_teacher })
                logVolumeCheckFailure('TOO_SOFT_AFTER_RETRIES', {
                    trialCount: currentCount,
                    volumeStats: volumeStats
                });
                setShowTeacherTroubleshootScreen(true)
            }
        }
    }

    const handleTooLoud = async (currentCount) => {
        studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_TEST_TOO_LOUD : VOLUME_CHECK_EVENTS.TOO_LOUD)

        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
        if(isTeacherMode){
            await speechProps.speakWithPromise({ textToSpeak: studentChecks.teacher_mode_volume_failure_one })
            setShowTeacherTroubleshootScreen(true)
        }else{
            studentPreCheck.addTroubleshootData({
                ready_handle_too_loud: {
                    volumeSnapshot: volumeStats,
                    trialCount: currentCount
                }
            })
            if(currentCount < 2){
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_loud_one })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_loud_two })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_too_loud_three })
                resetDidSaySomething()
                setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH)
            }else if(currentCount === 2){
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_get_teacher })
                logVolumeCheckFailure('TOO_LOUD_AFTER_RETRIES', {
                    trialCount: currentCount,
                    volumeStats: volumeStats
                });
                setShowTeacherTroubleshootScreen(true)
            }
        }
    }

    return null
}
